import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import Layout from '../Layout'

interface Account { id: number; name: string; slug: string }
interface Branch {
  id: number
  name: string
  address: string | null
  phone: string | null
  schedule: string | null
  is_active: boolean
}
interface Props {
  branches: Branch[]
  account: Account
  permissions?: { can_manage_branches: boolean }
  can_manage?: boolean
  can_delete?: boolean
}

export default function BranchesIndex({ branches, account, permissions, can_manage, can_delete }: Props) {
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const handleDelete = (id: number, name: string) => {
    if (!confirm(`¿Estás seguro de eliminar la sucursal "${name}"?`)) return
    setDeletingId(id)
    router.delete(`/${account.slug}/branches/${id}`, {
      preserveScroll: true,
      onFinish: () => setDeletingId(null),
    })
  }

  return (
    <Layout account={account} permissions={permissions}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#202020]">Sucursales</h1>
          <p className="text-sm text-gray-500 mt-1">
            {branches.length} {branches.length === 1 ? 'sucursal' : 'sucursales'}
          </p>
        </div>
        {can_manage && (
          <Link href={`/${account.slug}/branches/new`}
            className="bg-[#FFC107] text-[#202020] px-4 py-2 rounded hover:bg-[#e6ad06] transition-colors font-semibold">
            + Nueva Sucursal
          </Link>
        )}
      </div>

      {branches.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 text-lg mb-2">No hay sucursales</p>
          {can_manage && (
            <Link href={`/${account.slug}/branches/new`}
              className="inline-block mt-3 text-[#55ACEE] hover:underline font-medium">
              Crear la primera
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* Mobile: cards */}
          <div className="md:hidden space-y-3">
            {branches.map((b) => (
              <div key={b.id} className="bg-white rounded-lg shadow p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#202020] truncate">{b.name}</p>
                    {b.address && <p className="text-sm text-gray-500 truncate">{b.address}</p>}
                    {b.phone && <p className="text-sm text-gray-500">{b.phone}</p>}
                    {b.schedule && <p className="text-xs text-gray-400 mt-1">{b.schedule}</p>}
                    <p className="mt-1">
                      {b.is_active
                        ? <span className="text-xs text-green-600 font-medium">● Activa</span>
                        : <span className="text-xs text-gray-400 font-medium">● Inactiva</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                  {can_manage && (
                    <Link href={`/${account.slug}/branches/${b.id}/edit`}
                      className="text-[#55ACEE] hover:underline text-sm font-medium">Editar</Link>
                  )}
                  {can_delete && (
                    <button onClick={() => handleDelete(b.id, b.name)}
                      disabled={deletingId === b.id}
                      className="text-red-600 hover:underline text-sm font-medium disabled:opacity-50">
                      {deletingId === b.id ? 'Eliminando...' : 'Eliminar'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: table */}
          <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dirección</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teléfono</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Horario</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {branches.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-[#202020]">{b.name}</td>
                    <td className="px-6 py-4 text-gray-500">{b.address || '—'}</td>
                    <td className="px-6 py-4 text-gray-500">{b.phone || '—'}</td>
                    <td className="px-6 py-4 text-gray-500">{b.schedule || '—'}</td>
                    <td className="px-6 py-4">
                      {b.is_active
                        ? <span className="text-xs text-green-600 font-medium">Activa</span>
                        : <span className="text-xs text-gray-400">Inactiva</span>}
                    </td>
                    <td className="px-6 py-4 text-right space-x-3">
                      {can_manage && (
                        <Link href={`/${account.slug}/branches/${b.id}/edit`}
                          className="text-[#55ACEE] hover:underline">Editar</Link>
                      )}
                      {can_delete && (
                        <button onClick={() => handleDelete(b.id, b.name)}
                          disabled={deletingId === b.id}
                          className="text-red-600 hover:underline disabled:opacity-50">
                          {deletingId === b.id ? 'Eliminando...' : 'Eliminar'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Layout>
  )
}
